import _regeneratorRuntime from "/root/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

import get from 'lodash/get';
import { validateLoaderParams, validateReplacementParams } from './validate';
import { getOptions } from './utils';
import { cancelOnLocationChange } from './sagas';
import { LOADER } from './constants';
/**
 * A data loader to be used with {@link Loadable}
 * @global
 * @typedef {Function} DataLoader
 * @property {String} key - Unique identifier for the data loader
 * @property {Function} getKey - Getter function for the key property
 * @property {Function} toString - Replaces native `toString` method with one that
 * prints the loader key
 * @property {Function} replace - Method to shallowly copy data loader while replacing
 * one of the definition properties
 * @property {Boolean} {LOADER} - {@link LOADER} symbol attached to the data loader
 * to easily identify the function as a data loader
 */

/**
 * A loader definition that contains a `load` saga, and optionally a `listen` saga
 * and an `injectReducers` function.
 * @global
 * @typedef {Object} LoaderDefinition
 * @property {Saga} load - A saga that completes after data is loaded
 * @property {Saga} listen - A saga that sets up one or more action listeners
 * @property {Function} injectReducers - A function that takes in the `injectReducer`
 * @property {Array<DataLoader>} dependencies - Specify loaders that are dependencies
 */

/**
 * Helper utility that allows you to easily create a data loader to use with the
 * {@link Loadable} higher-order component.
 *
 * @param {String} key - Unique identifier for the loader
 *   - Used to generate keys for the `load`/`listen` sagas
 *   - Allows data loader to run sagas only once per route change
 * @param {LoaderDefinition} definition - Loader definition that defines the
 * behavior of the loader
 * @param  {Object} [replacements={}] - INTERNAL ONLY &mdash; used by `dataLoader.replace`
 * @returns {DataLoader} - A data loader to be used with {@link Loadable}
 */

export function createLoader(key, definition) {
  var replacements = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {}; // Validate the provided input params

  validateLoaderParams(key, definition);
  /**
   * Get the loader options for use below
   * @type {LoaderOptions}
   */

  var loaderOptions = getOptions(key, definition, replacements);
  /**
   * The data loader created to work with the `loaderInterface` provided by the
   * {@link Loadable} higher-order component. If an `injectReducers` function is
   * provided in the {@link createLoader} `definition` argument, the loader will
   * execute the function with `injectReducer` from the `loaderInterface`. If
   * a `load` and/or `listen` saga is provided, the loader will create a Symbol
   * based on the key provided (ex. `${key}Load`) and use `injectSaga` to run
   * the saga(s) once per location change. When the `LOCATION_CHANGE` action is
   * dispatched, the sagas are ejected and are able to run again (for example,
   * the next time a user visits the page).
   *
   * @param {Function} injectedDependencies - A list of loaders to inject and
   * replace all dependencies that share the same key &mdash; property of the
   * {@link loaderInterface}
   * @param {Function} injectReducer - Function to inject a reducer into the store
   * &mdash; property of the {@link loaderInterface}
   * @param {Function} injectSaga - Function to inject a saga into the store &mdash;
   * property of the {@link loaderInterface}
   * @param {Function} ejectSaga - Function to eject a saga from the store &mdash;
   * property of the {@link loaderInterface}
   * @param {Object} props - Props passed into the {@link Loadable}-wrapped
   * component &mdash; property of the {@link loaderInterface}
   * @param {...*} args - Additional arguments to pass to `load`/`listen` sagas
   * &mdash; NOTE: `props` from the {@link loaderInterface} will automatically
   * be provided as the first argument to the sagas by the data loader
   *
   * @returns {Promise} - A promise that resolves when the `load` saga completes
   *
   * @type {DataLoader}
   * @see {@link Loadable} to learn more about data loading
   * @see {@link loaderInterface} to learn more about what these params do
   */

  function dataLoader(_x) {
    return _dataLoader.apply(this, arguments);
  }

  function _dataLoader() {
    _dataLoader = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee(loaderInterface) {
      var _len,
          args,
          _key,
          _loaderInterface$inje,
          injectedDependencies,
          injectReducer,
          injectSaga,
          ejectSaga,
          props,
          injectedLoader,
          options,
          LISTEN,
          LOAD,
          CANCEL,
          load,
          listen,
          injectReducers,
          sagaKeys,
          dependencies,
          resolvedDependencies,
          loadPromise,
          _args = arguments;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              for (_len = _args.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
                args[_key - 1] = _args[_key];
              }

              _loaderInterface$inje = loaderInterface.injectedDependencies, injectedDependencies = _loaderInterface$inje === void 0 ? [] : _loaderInterface$inje, injectReducer = loaderInterface.injectReducer, injectSaga = loaderInterface.injectSaga, ejectSaga = loaderInterface.ejectSaga, props = loaderInterface.props; // Determine if this loader has been replaced by an injected loader with
              // the same key

              injectedLoader = injectedDependencies.find(function (dep) {
                return key === dep.key;
              }); // If loader has been replaced by an injected loader, use options from the
              // injected loader; otherwise fallback to using the current loader options

              options = get(injectedLoader, 'options', loaderOptions);
              LISTEN = options.LISTEN, LOAD = options.LOAD, CANCEL = options.CANCEL, load = options.load, listen = options.listen, injectReducers = options.injectReducers, sagaKeys = options.sagaKeys, dependencies = options.dependencies; // If the current loader has dependencies, ensure they are resolved properly
              // by mapping any replaced loaders to their injected counterparts

              if (!dependencies) {
                _context.next = 9;
                break;
              }

              resolvedDependencies = dependencies.map(function (dependency) {
                // Determine if replacement loader has been injected
                var injectedDependency = injectedDependencies.find(function (dep) {
                  return dependency.key === dep.key;
                }); // If one exists, return the injected dependency; otherwise, return
                // the unmodified dependency

                return injectedDependency || dependency;
              }); // Wait for all loader dependencies to resolve successfully

              _context.next = 9;
              return Promise.all(resolvedDependencies.map(function (loader) {
                return loader.apply(void 0, [loaderInterface].concat(args));
              }));

            case 9:
              // Pass `injectReducer` to the `injectReducers` function
              if (injectReducers) injectReducers(injectReducer); // Inject the `listen` saga if provided

              if (listen) injectSaga.apply(void 0, [LISTEN, {
                saga: listen
              }, props].concat(args)); // The `load` saga is what dictates that the loading is complete, so we use
              // it as the load promise that is returned

              loadPromise = null;
              if (load) loadPromise = injectSaga.apply(void 0, [LOAD, {
                saga: load
              }, props].concat(args)); // Cancel all of the sagas on location change by ejecting them

              injectSaga(CANCEL, {
                saga: cancelOnLocationChange
              }, ejectSaga, sagaKeys);
              return _context.abrupt("return", loadPromise);

            case 15:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return _dataLoader.apply(this, arguments);
  }

  dataLoader.key = key;
  dataLoader.options = loaderOptions;

  dataLoader.getKey = function () {
    return key;
  };

  dataLoader.toString = function () {
    return key;
  };
  /**
   * A replacement definition for a saga property on a loader.
   * @global
   * @typedef {Object} SagaReplacement
   * @property {String} key - The identifier to use in the saga key
   * @property {Saga} saga - A saga to replace either `load` or `listen`
   */

  /**
   * Allows you to make a copy of an existing data loader but replace one of the
   * loader definition properties.
   * @param {String} property - The property to replace
   *   - Ex: 'load'
   * @param {SagaReplacement|Function} replacement - Replacement for the provided
   * property. Can be a saga replacement or a function to replace `injectReducers`
   * @returns {DataLoader} - A new data loader with the replaced behavior
   */


  dataLoader.replace = function (property, replacement) {
    // Validate the provided input params
    validateReplacementParams(property, replacement);
    return createLoader(key, definition, Object.assign({}, replacements, _defineProperty({}, property, replacement)));
  };

  dataLoader[LOADER] = true;
  return dataLoader;
}
export default createLoader;